import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { LoggingService } from '@com-delta-idp/mnx-utils';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DeviceTypeHelper } from '../../common/device-type-helper';
import { ApplicationConfig } from '../../config/config';
import {
  AirportListLookupModel,
  AirportLookupModel
} from '../../model/airport-lookup.model';
import { ContentAirportLookup } from '../../model/content/contentAirportLookup.model';
import { RecentSearch } from '../../model/recent-search.model';
import { SavedsearchesModel } from '../../model/savedsearches.model';
import { CommonService } from '../../services/common.service';
import { ContentHeaderFooterService } from '../../services/content/contentheaderfooter.service';
import { AirportLookupModalComponent } from './airport-lookup/airport-lookup-modal-component/airport-lookup-modal.component';

@Directive({
  selector: '[ngc-airport-lookup-directive]',
  exportAs: 'ngcAirportLookupDirective'
})
export class AirportLookupDirective implements OnInit, OnDestroy, OnChanges {
  @Input() lookupTitle: string;
  @Input() triggeringElement;
  @Input() savedSearchesAvailable: boolean;
  @Input() savedSearches: SavedsearchesModel[];
  @Input() recentSearchsAvailable: boolean;
  @Input() recentSearchSelected: RecentSearch[];
  @Input() currentNav: string;
  @Input() initialLookupKey: string;
  @Input() initialLookupFromKey: string;
  @Input() tripType: string;
  @Input() widgetOnTop = false;
  @Output() selectedAirport = new EventEmitter<AirportLookupModel>();
  @Output() searchedAirportCode: EventEmitter<string> = new EventEmitter();
  @Output() savedFromToAirport: EventEmitter<any> = new EventEmitter();
  @Output() selectedRecentSearch: EventEmitter<RecentSearch> =
    new EventEmitter<RecentSearch>();

  selectedAirportValue: AirportListLookupModel;

  bsModalRef: BsModalRef;
  isClosed = false;
  modalOnHiddenSubscription: Subscription;
  contentHeaderFooterServiceSubscribe: Subscription;
  srvGetDeviceTypSubscription: Subscription;
  airportLookupContent: ContentAirportLookup;
  deviceTypeSubscribe: Subscription;
  config = {
    animated: false
  };

  public popupPostion: any = [];
  public deviceTypes: any = [];

  private unsubscribe: Subject<void> = new Subject();

  constructor(
    private modalService: BsModalService,
    private el: ElementRef,
    private ren: Renderer2,
    private contentService: ContentHeaderFooterService,
    private srv: CommonService,
    private cdf: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object,
    private _deviceTypeHelper: DeviceTypeHelper,
    private configObj: ApplicationConfig,
    private loggingService: LoggingService
  ) {}

  private _isSavedSearchAvailable(changes: SimpleChanges) {
    const savedSearchList =
      changes.savedSearches &&
      changes.savedSearches.currentValue &&
      changes.savedSearches.currentValue['savedSearchList'] === 'success';
    const savedTripsList =
      changes.savedSearches &&
      changes.savedSearches.currentValue &&
      changes.savedSearches.currentValue['savedTripsList'].length;
    const savedSearchesAvailable =
      changes.savedSearchesAvailable &&
      changes.savedSearchesAvailable.currentValue;
    return savedSearchList || !!savedTripsList || savedSearchesAvailable;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this._isSavedSearchAvailable(changes) &&
      isPlatformBrowser(this.platformId)
    ) {
      this.processAirPortLookUpModal();
    }
  }

  ngOnInit() {
    this.isClosed = false;
    if (isPlatformBrowser(this.platformId)) {
      this.contentService._isHeaderFooter
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((headerFooterFlag) => {
          if (headerFooterFlag) {
            if (this.contentService.contentHeaderFooter) {
              this.contentHeaderFooterServiceSubscribe =
                this.contentService.contentHeaderFooter
                  .pipe(takeUntil(this.unsubscribe))
                  .subscribe(
                    (data) => {
                      if (data) {
                        this.airportLookupContent = data.airportLookup;
                      }
                    },
                    (err) => {
                      this.loggingService.log('Caught Error: ' + err);
                    }
                  );
            }
          }
        });
      this.deviceTypeSubscribe = this._deviceTypeHelper
        .deviceDetection()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((resp) => {
          this.deviceTypes = resp;
        });
    }
  }

  getPopupPostion(elem) {
    let offsetLeft = 0;
    let offsetTop = 0;
    const rect = elem.getBoundingClientRect();
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    offsetLeft = rect.left + scrollLeft;
    offsetTop = rect.top;
    if (
      document.getElementsByClassName('airport-code')[0] !== undefined &&
      document.getElementsByClassName('airport-code')[0] !== null
    ) {
      const fromContainerHeight =
        document.getElementsByClassName('airport-code')[0].clientHeight;
      offsetTop = offsetTop + fromContainerHeight + 10;
    }
    this.popupPostion = { offsetLeft, offsetTop };
  }

  openLookupModal(scrollToTop) {
    if (!isPlatformBrowser(this.platformId) || !document || !window) {
      return;
    }
    const bodyScroll = document.documentElement.scrollTop;
    const viewportOfTab = window.matchMedia(
      '(min-width: 1194px) and (max-width: 1366px)'
    );

    this.modalOnHiddenSubscription = this.modalService.onHidden.subscribe(
      (reason: string) => {
        if (
          this.initialLookupKey === null &&
          this.selectedAirportValue == null
        ) {
          this.selectedAirport.emit(
            new AirportLookupModel(null, null, null, null, null)
          );
        }
        if (reason === 'esc') {
          this.isClosed = true;
        }
        if (this.isClosed) {
          this.el.nativeElement.focus();
          this.modalOnHiddenSubscription.unsubscribe();
          this.isClosed = false;
        }
      },
      (err) => {
        this.loggingService.log('Caught Error: ' + err);
      }
    );
    const obj = Object.assign({}, this.config, {
      class: 'search-flyout airport-lookup'
    });
    this.bsModalRef = this.modalService.show(AirportLookupModalComponent, obj);
    this.processAirPortLookUpModal();
    const htmlRoot = document.getElementsByTagName('html')[0];
    if (!(htmlRoot.className.indexOf('no-scroll') >= 0)) {
      htmlRoot.className += ' no-scroll';
    }
    if (htmlRoot && !this.widgetOnTop) {
      htmlRoot.style.overflow = 'hidden';
    }
    if (viewportOfTab && viewportOfTab.matches && scrollToTop) {
      window.scrollTo(0, 0);
    }
    if (isPlatformBrowser(this.platformId) && document) {
      const mobileWidgetModal = document.getElementsByTagName(
        'ngc-mobile-widget-modal'
      )[0];
      if (mobileWidgetModal === undefined || mobileWidgetModal === null) {
        this.srv.dragModalContainers(this.el, this.ren);
        document.documentElement.scrollTop = bodyScroll;
        this.srv.focusModalContainers(this.el, this.ren);
      }
    }
    if (this.configObj.get('APP_DISABLE_LEGACY') === 'false') {
      const getModalCont = document.getElementsByTagName('modal-container')[1];
      if (getModalCont) {
        this.ren.addClass(getModalCont, 'fresh-air');
      }
    }
    this.setFocus(document.body.querySelector('modal-container .modal-dialog'));
  }

  setFocus(element) {
    const focusableEls = element.querySelectorAll(
      'a[href], [tabindex], input, button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
    );
    const firstFocusableEl = focusableEls[0];
    const lastFocusableEl = focusableEls[focusableEls.length - 1];
    const KEYCODE_TAB = 9;

    element.addEventListener('keydown', function (e) {
      const isTabPressed = e.key === 'Tab' || e.keyCode === KEYCODE_TAB;

      if (!isTabPressed) {
        return;
      }

      if (e.shiftKey) {
        /* shift + tab */
        if (document.activeElement === firstFocusableEl) {
          lastFocusableEl.focus();
          e.preventDefault();
        }
      } /* tab */ else {
        if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus();
          e.preventDefault();
        }
      }
    });
  }

  getLookupTitle() {
    let title = 'Origin';
    if (this.lookupTitle) {
      if (this.lookupTitle.toLowerCase() === 'origin') {
        title = this.airportLookupContent
          ? this.airportLookupContent.originLabel
          : 'origin';
      } else if (this.lookupTitle.toLowerCase() === 'destination') {
        title = this.airportLookupContent
          ? this.airportLookupContent.destinationLabel
          : 'destination';
      }
    }
    return title;
  }

  selectAirport(airport) {
    this.selectedAirportValue = airport;
    this.initialLookupKey = airport.airportCode;
    this.selectedAirport.emit(airport);
    this.bsModalRef.hide();
  }

  getSearchedCode = (airportCode) => {
    this.searchedAirportCode.emit(airportCode);
  };

  close() {
    this.isClosed = true;
  }

  setRecentSelected(flag) {
    this.selectedRecentSearch.emit(flag);
  }

  setSavedSearch(flag) {
    this.savedFromToAirport.emit(flag);
  }

  recalculatePosition() {
    if (this.bsModalRef) {
      if (this.deviceTypes.isDesktop) {
        if (isPlatformBrowser(this.platformId)) {
          this.getPopupPostion(this.el.nativeElement);
          const el = document.getElementsByClassName('modal-dialog')[0];
          const bodyElWidth =
            document.getElementsByTagName('body')[0].clientWidth - 17;
          const popupAreaWidth = this.popupPostion.offsetLeft + el.clientWidth;
          if (popupAreaWidth >= bodyElWidth) {
            const popupLeftDiff = popupAreaWidth - bodyElWidth;
            this.popupPostion.offsetLeft =
              this.popupPostion.offsetLeft - popupLeftDiff;
          }
          const popupAreaHeight = this.popupPostion.offsetTop + el.clientHeight;
          if (popupAreaHeight >= window.innerHeight) {
            const popupTopDiff = popupAreaHeight - window.innerHeight;
            this.popupPostion.offsetTop =
              this.popupPostion.offsetTop - popupTopDiff;
          }
          this.ren.setAttribute(
            el,
            'style',
            'margin-left:' +
              this.popupPostion.offsetLeft +
              'px; margin-top:' +
              this.popupPostion.offsetTop +
              'px;'
          );
        }
      }
    }
  }

  ngOnDestroy() {
    if (this.modalOnHiddenSubscription) {
      this.modalOnHiddenSubscription.unsubscribe();
    }
    if (this.contentHeaderFooterServiceSubscribe) {
      this.contentHeaderFooterServiceSubscribe.unsubscribe();
    }
    if (this.srvGetDeviceTypSubscription) {
      this.srvGetDeviceTypSubscription.unsubscribe();
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this.unsubscribe.unsubscribe();
    if (this.deviceTypeSubscribe) {
      this.deviceTypeSubscribe.unsubscribe();
    }
  }

  processAirPortLookUpModal() {
    if (this.bsModalRef) {
      if (this.deviceTypes.isDesktop || this.deviceTypes.isTablet) {
        if (isPlatformBrowser(this.platformId)) {
          const bodyRefEl = document.body.classList.contains('profile');
          const modalRefEl = document.querySelector('modal-container');
          if (bodyRefEl && modalRefEl) {
            modalRefEl.classList.add('fresh-air');
          }
          this.getPopupPostion(this.el.nativeElement);
          const el = document.body.querySelector(
            'modal-container .modal-dialog'
          );
          if (el.clientWidth === 0) {
            this.ren.addClass(el.parentElement, 'fresh-air');
            this.ren.setAttribute(
              el.parentElement,
              'style',
              'display : block !important'
            );
          }
          const bodyElWidth =
            document.getElementsByTagName('body')[0].clientWidth - 17;
          const popupAreaWidth = this.popupPostion.offsetLeft + el.clientWidth;
          if (popupAreaWidth >= bodyElWidth) {
            const popupLeftDiff = popupAreaWidth - bodyElWidth;
            this.popupPostion.offsetLeft =
              this.popupPostion.offsetLeft - popupLeftDiff;
          }
          const popupAreaHeight = this.popupPostion.offsetTop + el.clientHeight;
          if (popupAreaHeight >= window.innerHeight) {
            const popupTopDiff = popupAreaHeight - window.innerHeight;
            this.popupPostion.offsetTop =
              this.popupPostion.offsetTop - popupTopDiff;
          }
          this.ren.setAttribute(
            el,
            'style',
            'margin-left:' +
              this.popupPostion.offsetLeft +
              'px; margin-top:' +
              this.popupPostion.offsetTop +
              'px;'
          );
          this.ren.setAttribute(
            el.parentElement,
            'aria-labelledby',
            'modalTitle'
          );
        }
      }
      this.bsModalRef.content.title = this.lookupTitle;
      this.bsModalRef.content.airportLookupDir = this;
      this.bsModalRef.content.searchField.nativeElement.focus();
      if (this.initialLookupKey) {
        this.bsModalRef.content.selectedValue = this.initialLookupKey;
      }
      this.bsModalRef.content.airportLookupContent = this.airportLookupContent;
      this.bsModalRef.content.title = this.getLookupTitle();
      if (
        this.currentNav === 'Check-In' ||
        this.currentNav === 'Flight-Status'
      ) {
        this.bsModalRef.content.displayRecentSaveSearch = false;
      } else {
        this.bsModalRef.content.displayRecentSaveSearch = true;
      }
      this.bsModalRef.content.savedSearchesAvailableFlag =
        this.savedSearchesAvailable !== undefined
          ? this.savedSearchesAvailable
          : false;
      this.bsModalRef.content.savedTrips =
        this.savedSearches !== undefined ? this.savedSearches : false;
      this.bsModalRef.content.recentSearchesAvailableFlag =
        this.recentSearchsAvailable !== undefined
          ? this.recentSearchsAvailable
          : false;
      this.bsModalRef.content.recentSearchSelected =
        this.recentSearchSelected !== undefined
          ? this.recentSearchSelected
          : false;
      this.cdf.detectChanges();
    }
  }
}
